import { useEffect, useState } from 'react'

export interface ReviewDraft {
  verdict: '' | 'ready' | 'needs_changes'
  feedback: string
}
const empty: ReviewDraft = { verdict: '', feedback: '' }

/** Personal, unsent feedback survives navigation; submitted reviews remain server-owned. */
export function usePeerReviewDraft(userId: string, roundId: string, example?: ReviewDraft) {
  const key = `seldon.peer-review.v1:${userId}:${roundId}`
  const [draft, setDraft] = useState<ReviewDraft>(() => {
    if (example) return example
    try {
      const saved = JSON.parse(localStorage.getItem(key) ?? 'null')
      if (
        saved &&
        ['', 'ready', 'needs_changes'].includes(saved.verdict) &&
        typeof saved.feedback === 'string'
      ) {
        // Fold any unsent comment from the earlier preview into the single feedback box.
        const comment = typeof saved.comment === 'string' ? saved.comment.trim() : ''
        const reference = saved.reference
        const context = [
          typeof reference?.criterion_id === 'string' ? reference.criterion_id : '',
          typeof reference?.step === 'string' ? `Rollout step ${reference.step}` : '',
        ]
          .filter(Boolean)
          .join(' · ')
        const feedback = comment
          ? [saved.feedback, [context, comment].filter(Boolean).join(': ')].filter(Boolean).join('\n\n')
          : saved.feedback
        return { verdict: saved.verdict, feedback }
      }
    } catch {
      /* Storage can be unavailable; editing still works in memory. */
    }
    return empty
  })
  useEffect(() => {
    if (example) return
    try {
      if (draft.verdict || draft.feedback) localStorage.setItem(key, JSON.stringify(draft))
      else localStorage.removeItem(key)
    } catch {
      /* Keep the current draft usable if browser storage is full. */
    }
  }, [key, draft, example])
  function update<K extends keyof ReviewDraft>(field: K, value: ReviewDraft[K]) {
    setDraft((current) => ({ ...current, [field]: value }))
  }
  return { draft, update, clear: () => setDraft(empty) }
}
